import { CSSProperties } from 'react';
import { CARD_SIZE, CARD_VARIANT } from 'enums';

export type CardVariant = `${CARD_VARIANT}`;
export type CardSize = `${CARD_SIZE}`;

interface CardProps {
  children: React.ReactNode;
  variant?: CardVariant;
  size?: CardSize;
  className?: string;
  style?: CSSProperties;
  onClick?: (
    event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>
  ) => void;
  interactive?: boolean;
  'aria-pressed'?: boolean;
}

const Card: React.FC<CardProps> = ({
  children,
  variant = CARD_VARIANT.DEFAULT,
  size = CARD_SIZE.MEDIUM,
  className = '',
  style,
  onClick,
  interactive = false,
  'aria-pressed': ariaPressed
}) => {
  const sizeClasses: Record<CardSize, string> = {
    [CARD_SIZE.SMALL]: '~p-2/4',
    [CARD_SIZE.MEDIUM]: '~p-3/6',
    [CARD_SIZE.LARGE]: '~p-4/8'
  };

  const variantClasses: Record<
    CardVariant,
    { container: string; interactive: string }
  > = {
    [CARD_VARIANT.DEFAULT]: {
      container:
        'bg-white dark:bg-secondary-dark-200 rounded-lg shadow border border-secondary-400/20 dark:border-secondary-dark-400',
      interactive: 'hover:shadow-md transition-shadow duration-200'
    },
    [CARD_VARIANT.OUTLINED]: {
      container:
        'bg-transparent rounded-lg border-2 border-secondary-400/60 dark:border-secondary-dark-500 shadow-none',
      interactive:
        'hover:border-primary-500/60 dark:hover:border-primary-dark-500 transition-colors duration-200'
    },
    [CARD_VARIANT.ELEVATED]: {
      container:
        'bg-white dark:bg-secondary-dark-200 rounded-lg shadow-lg border-0',
      interactive: 'hover:shadow-xl transition-shadow duration-200'
    },
    [CARD_VARIANT.FILLED]: {
      container:
        'bg-gray-50 dark:bg-secondary-dark-300 rounded-lg shadow border border-gray-200 dark:border-secondary-dark-500',
      interactive:
        'hover:bg-gray-100 dark:hover:bg-secondary-dark-400 transition-colors duration-200'
    },
    [CARD_VARIANT.MINIMAL]: {
      container:
        'bg-transparent rounded-lg shadow-none border border-secondary-400/30 dark:border-secondary-dark-500',
      interactive:
        'hover:bg-gray-50/50 dark:hover:bg-secondary-dark-300/50 transition-colors duration-200'
    },
    [CARD_VARIANT.GHOST]: {
      container: 'bg-transparent rounded-lg shadow-none border-0',
      interactive:
        'hover:bg-gray-50/30 dark:hover:bg-secondary-dark-300/30 transition-colors duration-200'
    }
  };

  const currentSize = sizeClasses[size];
  const currentVariant = variantClasses[variant];

  const baseClasses = `w-full ${currentSize} ${currentVariant.container} ${interactive ? currentVariant.interactive : ''} ${onClick || interactive ? 'cursor-pointer' : ''}`;

  const Component = onClick || interactive ? 'button' : 'div';

  return (
    <Component
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      aria-pressed={onClick != null || interactive ? ariaPressed : undefined}
      style={style}
      className={`${baseClasses} ${className}`}>
      {children}
    </Component>
  );
};

export default Card;
